import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

// Libs
import { useTranslation } from 'next-i18next';

// Utils
import { cn } from 'utils';
import srcSet from '../../../src/utils/srcSet';

// Hooks
import useCarousel from '../../../src/hooks/useCarousel';
import useLightbox from '../../../src/hooks/useLightbox';

// Components
import { Picto } from '../../atoms/Picto/Picto';

// Styles
import styles from './Lightbox.module.css';

function Lightbox({ children, images, className }) {
  const { t } = useTranslation();
  const [activeMedia, setActiveMedia] = useState(0);

  const {
    activeLists,
    emblaRef,
    prevBtnEnabled,
    nextBtnEnabled,
    scrollPrev,
    scrollNext,
    scrollTo,
    selectedIndex,
  } = useCarousel(images, { activeMedia: 1 });

  const { isOpened, setIsOpened } = useLightbox({
    emblaRef,
    prevBtnEnabled,
    nextBtnEnabled,
    scrollPrev,
    scrollNext,
    scrollTo,
  });

  const handleClickImage = (index) => {
    setActiveMedia(index);
    setIsOpened(true);
  };

  useEffect(() => {
    scrollTo(activeMedia, true);
  }, [scrollTo, activeMedia]);

  // check if children id a defined function
  return typeof children === 'function' && document?.body ? (
    <>
      <div
        role="presentation"
        className={cn([className || '', 'cursor-pointer'])}
      >
        {children(handleClickImage)}
      </div>

      {images?.length ? (
        createPortal(
          <div className={cn([styles.lightbox, isOpened ? styles.opened : ''])}>
            <div
              role="presentation"
              className={styles.back_layer}
              onClick={() => setIsOpened(false)}
            />

            <div
              className={cn([
                styles.wrapper,
                'd-flex d-col a-stretch',
                images.length === 1 ? styles.only_one : '',
              ])}
            >
              <div className="d-flex a-stretch j-space-btw flex-auto">
                {images.length > 1 ? (
                  <button
                    type="button"
                    className={cn([styles.nav, styles.prev])}
                    onClick={scrollPrev}
                    disabled={!prevBtnEnabled}
                    aria-label={t('common.previous')}
                  >
                    <Picto icon="arrowLeft" />
                  </button>
                ) : null}

                {isOpened ? (
                  <div className="w-100">
                    <div className={cn([styles.window, 'd-flex d-col j-center'])}>
                      <div className="d-flex a-center j-space-btw w-100">
                        <div className="p2-desktop-default text-center font-color-white">
                          {activeLists?.length ? (
                            `${selectedIndex + 1} / ${activeLists.length}`
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className={cn([styles.close, 'd-flex a-center p2-desktop-default font-color-white'])}
                          onClick={() => setIsOpened(false)}
                        >
                          {t('common.close')}
                          <Picto icon="cross" className="margin-left-8" />
                        </button>
                      </div>

                      {activeLists?.length ? (
                        <div className={cn([styles.carousel, 'carousel'])} ref={emblaRef}>
                          <div className={styles.images}>
                            {activeLists.map((item, index) => (
                              <div key={index} className={cn([styles.slide, 'carouselSlide'])}>
                                <div className={cn([styles.slideCard, 'slideCard'])}>
                                  <div className={cn([styles.slideContainer, 'slideContainer'])}>
                                    <img
                                      loading="eager"
                                      src={`${item.url}?w=1440&format=webp&q=75`}
                                      alt={item?.credit}
                                      sizes="(max-width: 1024px) 75vw,
                                                                 50vw"
                                      srcSet={srcSet(item.url)}
                                    />

                                    {item.credit ? (
                                      <div
                                        className={cn([
                                          styles.legend,
                                          'w-100 margin-top-8 p2-desktop-default font-color-white p2-mobile-default',
                                        ])}
                                      >
                                        {item.credit}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {images.length > 1 ? (
                  <button
                    type="button"
                    className={cn([styles.nav, styles.next])}
                    onClick={scrollNext}
                    disabled={!nextBtnEnabled}
                    aria-label={t('common.next')}
                  >
                    <Picto icon="arrowRight" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body,
        )
      ) : null}
    </>
  ) : null;
}

Lightbox.propTypes = {
  children: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
    credit: PropTypes.string,
  })),
  className: PropTypes.string,
};

Lightbox.defaultProps = {
  images: [],
  className: null,
};

export default Lightbox;
